import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";

/**
 * Tiny info-tooltip trigger. Uses a portal + position:fixed so the tooltip can
 * NEVER be clipped by an ancestor's overflow / border-radius. Auto-flips
 * horizontally if the panel would overflow the viewport.
 */
const TOOLTIP_W = 256;       // 16rem
const GAP = 8;               // px between trigger and tooltip
const VIEW_PADDING = 12;     // min distance from viewport edge

export default function InfoHint({ label, children }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const place = () => {
    const t = triggerRef.current;
    if (!t) return;
    const r = t.getBoundingClientRect();
    // Default: right of trigger, vertically centered.
    let left = r.right + GAP;
    let top = r.top + r.height / 2;
    // If overflow on the right, flip to left.
    if (left + TOOLTIP_W + VIEW_PADDING > window.innerWidth) {
      left = r.left - GAP - TOOLTIP_W;
    }
    // Final clamp inside viewport.
    if (left < VIEW_PADDING) left = VIEW_PADDING;
    if (top < VIEW_PADDING + 40) top = VIEW_PADDING + 40;
    if (top > window.innerHeight - VIEW_PADDING - 40) top = window.innerHeight - VIEW_PADDING - 40;
    setCoords({ top, left });
  };

  useLayoutEffect(() => {
    if (open) place();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => place();
    const onClick = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        tooltipRef.current && !tooltipRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const tooltip = open ? createPortal(
    <div
      ref={tooltipRef}
      role="tooltip"
      data-testid="info-hint-tooltip"
      style={{
        position: "fixed",
        top: coords.top,
        left: coords.left,
        transform: "translateY(-50%)",
        width: TOOLTIP_W,
        zIndex: 9999,
        pointerEvents: "none",
      }}
    >
      <div
        className="bg-black/95 border border-cyan-400/50 rounded-md p-3 text-mute text-xs gps-mono tracking-wide leading-relaxed shadow-[0_0_24px_rgba(0,240,255,0.25)] backdrop-blur-sm"
        style={{ pointerEvents: "auto" }}
      >
        {children}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={(e) => { e.stopPropagation(); setOpen((s) => !s); }}
        aria-label={label || "Info"}
        data-testid="info-hint-trigger"
        className="inline-grid place-items-center align-middle w-5 h-5 rounded-full text-mute hover:text-cyan transition-colors focus:outline-none focus:ring-1 focus:ring-cyan-400/50"
      >
        <Info size={14} strokeWidth={1.75} />
      </button>
      {tooltip}
    </>
  );
}
